import { useHandler } from "@redotech/react-util/hook";
import { RedoSingleDatePicker } from "@redotech/redo-web/arbiter-components/date-picker/redo-single-date-picker";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import {
  dateToPlainDate,
  plainDateToDate,
} from "@redotech/redo-web/timetable/common/utils/date-conversion";
import { memo, useState } from "react";

interface InitialFeeModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (fee: number, turnedOnAt: Date) => void;
}

const getStartOfCurrentBillingPeriod = (): Temporal.PlainDate => {
  return dateToPlainDate(new Date()).with({ day: 1 });
};

export const InitialFeeModal = memo(function InitialFeeModal({
  open,
  onClose,
  onSubmit,
}: InitialFeeModalProps) {
  const [fee, setFee] = useState<string>("");
  const [turnedOnAt, setTurnedOnAt] = useState<Date>(new Date());

  const handleClose = useHandler(() => {
    setFee("");
    setTurnedOnAt(new Date());
    onClose();
  });

  const handleSubmit = useHandler(() => {
    const feeValue = parseFloat(fee);
    if (isNaN(feeValue) || feeValue < 0) {
      return;
    }
    onSubmit(feeValue, turnedOnAt);
    setFee("");
    setTurnedOnAt(new Date());
  });

  const isValidFee =
    fee !== "" && !isNaN(parseFloat(fee)) && parseFloat(fee) >= 0;

  return (
    <RedoModal
      isOpen={open}
      onModalCloseRequested={handleClose}
      primaryButton={{
        text: "Set Fee",
        onClick: handleSubmit,
        disabled: !isValidFee,
      }}
      secondaryButton={{ text: "Cancel", onClick: handleClose }}
      title="Set Monthly Subscription Fee"
    >
      <Flex dir="column" gap="lg">
        <Text fontSize="sm" textColor="secondary">
          Enter the monthly subscription fee for OMS and the date when billing
          should start.
        </Text>
        <RedoTextInput
          label="Monthly Fee ($)"
          placeholder="e.g., 99"
          setValue={setFee}
          value={fee}
        />
        <Flex align="center" dir="row" gap="md">
          <Text fontSize="sm">Billing Start Date:</Text>
          <RedoSingleDatePicker
            buttonSize="sm"
            date={dateToPlainDate(turnedOnAt)}
            minDate={getStartOfCurrentBillingPeriod()}
            setDate={(date) => setTurnedOnAt(plainDateToDate(date))}
          />
        </Flex>
      </Flex>
    </RedoModal>
  );
});
